import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, User2 } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { NotificationPreferences } from '@/components/settings/NotificationPreferences';
import { Integrations } from '@/components/settings/Integrations';
import { Security } from '@/components/settings/Security';
import { ActivityLog } from '@/components/settings/ActivityLog';
import { HelpSupport } from '@/components/settings/HelpSupport';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';
import { getFullName } from '@/lib';
import { useAppSelector } from '@/store/hooks';
import {
  selectActiveOrganization,
  selectActiveOrganizationId,
  selectCurrentUserProfile,
} from '@/store/selectors/authorizationSelectors';

const SettingsPage = () => {
  const { hasAnyPermission, isSuperAdmin } = usePermissions();
  const profile = useAppSelector(selectCurrentUserProfile);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const canViewOrganization = isSuperAdmin || hasAnyPermission([Permissions.MyOrgRead, Permissions.MyOrgUpdate]);
  const organizationDetailsHref =
    canViewOrganization && activeOrganizationId
      ? `/admin/organizations/${encodeURIComponent(activeOrganizationId)}`
      : null;
  const profileLabel = profile ? getFullName(profile) : 'Complete your personal profile';
  const profileMeta = profile?.email ?? 'Open your account profile details and onboarding fields.';
  const organizationLabel = activeOrganization?.name ?? 'No active organization';
  const organizationMeta = organizationDetailsHref
    ? 'Open your organization details page.'
    : activeOrganizationId
      ? 'You are connected to an organization, but you do not have access to its details page.'
      : 'No active organization is currently selected.';

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" />

      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <User2 className="h-4 w-4" />
                  Profile Details
                </CardTitle>
                <CardDescription>{profileMeta}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">{profileLabel}</p>
                <p className="text-muted-foreground text-sm">{profile?.phone ?? 'Phone number not added yet.'}</p>
              </div>

              <Button asChild className="w-full">
                <Link to="/settings/profile">
                  Edit Profile
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Organization Details
                </CardTitle>
                <CardDescription>{organizationMeta}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">{organizationLabel}</p>
                <p className="text-muted-foreground text-sm">
                  {activeOrganizationId ?? 'No organization ID available.'}
                </p>
              </div>

              {organizationDetailsHref ? (
                <Button asChild className="w-full">
                  <Link to={organizationDetailsHref} state={{ fromSettings: true }}>
                    Open Organization
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button className="w-full" disabled>
                  Open Organization
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        <NotificationPreferences />
        <Integrations />
        <Security />
        <ActivityLog />
        <HelpSupport />
      </div>
    </div>
  );
};

export default SettingsPage;
